import { useState } from "react";
import Card from "./Card";
import Button from "Components/Common/Button/Button";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import { useToast } from "Components/Common/Toast/ToastContext";
import { DrawDetail, fmtMoney } from "../DrawHistory.types";
import { formatDate } from "utils/Common/Date";
import NoDocuments from "Assets/Img/NoDocuments";
import EmptyState from "Components/Common/EmptyState";
import { downloadDocumentBlob } from "services/projectService";
import { triggerBlobDownload } from "utils/Common/fileDownload";

interface InvoicesTabProps {
  drawDetail: DrawDetail | null;
  loading: boolean;
  projectId: string;
}

const InvoicesTab = ({ drawDetail, loading, projectId }: InvoicesTabProps) => {
  const showToast = useToast();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const items = (drawDetail?.lienWaivers?.items ?? []).filter(
    (item) => item?.invoice,
  );

  const handleDownload = async (
    itemId?: string,
    documentId?: string,
    fileName?: string,
  ) => {
    if (!documentId) {
      showToast("Invoice document unavailable.");
      return;
    }
    setDownloadingId(itemId ?? documentId);
    const blob = await downloadDocumentBlob(projectId, documentId);
    setDownloadingId(null);
    if (!blob) {
      showToast("Failed to download invoice. Please try again.");
      return;
    }
    triggerBlobDownload(blob, fileName ?? "invoice.pdf");
  };

  return (
    <>
      <div className="sectionTitle">
        Invoices &amp; Supporting Documentation
      </div>
      <div className="sectionSub">
        All invoices submitted by the GC with Draw #{" "}
        {drawDetail ? String(drawDetail.draw.drawNumber).padStart(2, "0") : "—"}
        . Click to download individual files.
      </div>

      <Card title={`Submitted Invoices (${items.length})`}>
        {loading ? (
          Array.from({ length: 2 }).map((_, i) => (
            <div className="docItem" key={i}>
              <Skeleton width={36} height={36} borderRadius={8} />
              <div className="docBody">
                <Skeleton
                  width="60%"
                  height={13}
                  borderRadius={4}
                  style={{ marginBottom: 6 }}
                />
                <Skeleton width="40%" height={11} borderRadius={4} />
              </div>
              <Skeleton width={90} height={28} borderRadius={5} />
            </div>
          ))
        ) : items.length === 0 ? (
          <EmptyState
            noBorder
            icon={<NoDocuments />}
            title="No invoices attached to this draw."
          />
        ) : (
          items.map((item) => (
            <div className="docItem" key={item?.id}>
              <div className="docIcon pdf">
                {item?.invoice?.fileName?.split(".").pop()?.toUpperCase() ??
                  "DOC"}
              </div>
              <div className="docBody">
                <div className="docName">
                  {item?.invoice?.fileName ?? item?.description ?? "Invoice"}
                </div>
                <div className="docMeta">
                  {[
                    item?.subContractor?.companyName ?? item?.companyName,
                    fmtMoney(item?.totalAmount),
                    item?.invoice?.uploadedAt
                      ? `Uploaded ${formatDate(item?.invoice.uploadedAt, false)}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              <Button
                type="button"
                className="docBtn"
                disabled={
                  downloadingId === (item?.id ?? item?.invoice?.documentId)
                }
                onClick={() =>
                  handleDownload(
                    item?.id,
                    item?.invoice?.documentId,
                    item?.invoice?.fileName,
                  )
                }
              >
                {downloadingId === (item?.id ?? item?.invoice?.documentId)
                  ? "Downloading..."
                  : "Download"}
              </Button>
            </div>
          ))
        )}
      </Card>
    </>
  );
};

export default InvoicesTab;
